import { fetchDocumentPartNodeWithPermissions } from "../../api/document/DocumentsPart";
import { DocumentRoot } from "../../api/models/document-root";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { mapFormToDataItem, TreeViewDataItem } from "./DocumentPartTree";
import { DocumentPosition } from "../../api/models/document-position";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
const copyItemsAndItem = (
  items: TreeViewDataItem[],
  indexes: number[]
): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
  const copiedItems = items.slice();
  const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

  if (indexes.length > 1) {
    const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
    item.items = children;
    return { items: copiedItems, item: target };
  } else {
    return { items: copiedItems, item: item };
  }
};

function getPaginatedIndex(id: string, page: number) {
  let pos = id.lastIndexOf("_");
  let showMorePosition = page * 20;
  if (pos < 0) {
    return "0_" + showMorePosition;
  } else {
    return id + "_" + showMorePosition;
  }
}

export const fetchExpandedNodeExternal = async (
  hierarchicalIndexToExpand: string,
  isReload: boolean,
  page: number,
  isInitialization: boolean,
  originalTreeItems: TreeViewDataItem[],
  setElementToUpdate: (value: ((prevState: DocumentRoot[]) => DocumentRoot[]) | DocumentRoot[]) => void
) => {
  let hierarchicalIndex = hierarchicalIndexToExpand;
  if (hierarchicalIndex && isInitialization && page !== 0) {
    hierarchicalIndex = getPaginatedIndex(hierarchicalIndex, page);
  }
  if (!hierarchicalIndex || hierarchicalIndex.length === 0) return [];
  const indexes = hierarchicalIndex.split("_").map((value) => Number(value)) ?? [];
  if (page !== 0) {
    indexes.pop();
  }
  const { items, item } = copyItemsAndItem(originalTreeItems, indexes);
  item.expanded = isReload ? true : !item.expanded;
  if (!isReload && item.items && page === 0) {
    return items;
  }
  const dataPart = await fetchDocumentPartNodeWithPermissions(item.key, page);
  const childrenDocumentPart: DocumentRoot[] = dataPart._embedded?.content ?? [];
  setElementToUpdate(() => [...childrenDocumentPart]);

  if (page !== 0) {
    item.expanded = true;
    item.data.subparts = item.data.subparts.concat(childrenDocumentPart);
    item.items.pop();
    item.items = item.items.concat(childrenDocumentPart.map(mapFormToDataItem));
  } else {
    item.data.subparts = childrenDocumentPart;
    item.items = childrenDocumentPart.map(mapFormToDataItem);
  }

  item.items.sort((a, b) => a.order - b.order);

  if (dataPart.page.totalPages != page + 1 && dataPart._embedded?.content) {
    item.items.push({
      text: formatMessage("cof.documentslist.showmore"),
      key: "key",
      order: 1,
      hasChildren: false,
      nodeType: "pagination",
      data: { ...dataPart.page, idParent: item.key },
    });
  }

  item.hasChildren = false; // hasChildren is need only for lazy loading
  return items;
};

export const fetchInitialDocumentPartTreeProps = async (
  initialTreeItem: FlatPathTreeItem,
  rootId: string,
  parentsPositions: DocumentPosition[],
  setElementToUpdate: (value: ((prevState: DocumentRoot[]) => DocumentRoot[]) | DocumentRoot[]) => void
) => {
  if (!rootId.length) return;

  let treeItems: TreeViewDataItem[] = [
    {
      text: initialTreeItem.document.name,
      key: initialTreeItem.document.id,
      order: initialTreeItem.document.order,
      hasChildren: initialTreeItem.document.hasChildren,
      nodeType: "part",
      expanded: false,
      items: undefined,
      data: initialTreeItem.document,
      hasReadPermissions: initialTreeItem.readPermission,
    },
  ];

  let hierarchicalIndex = "0";
  if (parentsPositions?.length)
    for (const position of parentsPositions) {
      for (let currentPage = 0; currentPage <= position.page; currentPage++) {
        treeItems = await fetchExpandedNodeExternal(
          hierarchicalIndex,
          false,
          currentPage,
          true,
          treeItems,
          setElementToUpdate
        );
      }
      hierarchicalIndex += `_${position.order}`;
    }
  treeItems = await fetchExpandedNodeExternal(hierarchicalIndex, false, 0, true, treeItems, setElementToUpdate);

  return treeItems;
};
